import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

/**
 * Alamat kontak yang disebut di kedua dokumen legal.
 * Satu tempat supaya Privacy dan Terms tidak pernah menyebut alamat berbeda.
 */
export const KONTAK = 'support@orisini.com'

/** Tanggal berlaku. Ubah setiap kali isi dokumen berubah secara berarti. */
export const BERLAKU = '4 Nov 2026'

function Tautan({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="rounded-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      {children}
    </Link>
  )
}

/**
 * Kerangka halaman Privacy dan Terms.
 *
 * Publik dan tanpa sesi: dokumen ini harus terbaca SEBELUM orang mencentang
 * persetujuan di halaman daftar. Navigasinya sengaja tidak memakai SitusNav —
 * tautan jangkar di sana (#pricing dst.) hanya berarti di halaman depan.
 */
export function HalamanLegal({ judul, ringkas, children }: { judul: string; ringkas: string; children: ReactNode }) {
  return (
    <div className="min-h-dvh bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex w-full max-w-3xl items-center gap-2.5 px-6 py-3">
          <Link
            to="/"
            className="flex items-center gap-2.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <img src="/favicon.svg" alt="" className="size-6" />
            <span className="text-base font-semibold tracking-tight">Orisini</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-6 py-12 lg:py-16">
        <h1 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl">{judul}</h1>
        <p className="mt-3 text-sm text-muted-foreground">Effective {BERLAKU}</p>
        <p className="mt-6 max-w-[65ch] text-pretty text-muted-foreground">{ringkas}</p>

        <div className="mt-10 space-y-10">{children}</div>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex w-full max-w-3xl flex-wrap gap-x-6 gap-y-2 px-6 py-8 text-sm">
          <span className="text-muted-foreground">© {new Date().getFullYear()} Orisini</span>
          <Tautan to="/privacy">Privacy Policy</Tautan>
          <Tautan to="/terms">Terms of Service</Tautan>
        </div>
      </footer>
    </div>
  )
}

/** Satu pasal bernomor. `id` dipakai untuk tautan langsung ke pasal tertentu. */
export function Pasal({ id, judul, children }: { id: string; judul: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-6">
      <h2 className="text-lg font-semibold tracking-tight">{judul}</h2>
      <div className="mt-3 max-w-[65ch] space-y-3 text-sm leading-relaxed text-muted-foreground [&_strong]:font-medium [&_strong]:text-foreground [&_ul]:list-disc [&_ul]:space-y-1.5 [&_ul]:pl-5">
        {children}
      </div>
    </section>
  )
}

/** Tautan email kontak dengan gaya yang sama di kedua dokumen. */
export function Surel() {
  return (
    <a href={`mailto:${KONTAK}`} className="text-foreground underline underline-offset-4">
      {KONTAK}
    </a>
  )
}
